import React, { useState } from 'react';
import { userRoles, roleList } from '../utility/ReUsableFunctions';
import { LoggedUser, MenuItem } from './types';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles.css';
import mainMenuItems from './MainMenuItems';
import communityMenuItems from './CommunityMenuItems';
/**
 * NavMenu component renders a sidebar navigation menu with expandable submenus.
 * It checks user roles to determine which menu items to display.
 *
 * @param {LoggedUser} loggedUser - The logged-in user object containing roles.
 * @returns {JSX.Element} The rendered NavMenu component.
 */
const NavMenu: React.FC<{ loggedUser: LoggedUser}> = ({ loggedUser } ) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState<string>('');

  const toggleSubmenu = (itemId: string) => {
    if (itemId === activeItem) {
      setActiveItem('');
      return;
    }

    setActiveItem(itemId);
  };

  const isMenuItemAuthorized = (menuItem: MenuItem) => {
    if (Array.isArray(menuItem.children) && menuItem.children.length > 0) {
      // If any child is authorized, then the menu item is authorized
      return menuItem.children.some(child => isMenuItemAuthorized(child));
    }
    
    const userRolesList = roleList(loggedUser?.roles);
    const hasItemRequiredRoles = Array.isArray(menuItem.requiredRoles);
    
    return (
      (hasItemRequiredRoles && menuItem.requiredRoles.includes(userRoles.ANY_USER)) ||
      (hasItemRequiredRoles && menuItem.requiredRoles.some(role => userRolesList.includes(role)))
    );
  }; 

  /**
   * Taking account the URL, load the menu items.
   * If the URL is The Loop page, load the community menu items.
   * If the URL is not The Loop page, load the main menu items.
   * @param {string} url - The current URL.
   * @returns {MenuItem[]} - The filtered array of menu items.
   */
  const getMenuItems = () => window.location.href.includes('/the-loop') 
    ? communityMenuItems
    : mainMenuItems;

  return (
    <div className={`sidebar text-white ${isExpanded ? 'expanded' : 'collapsed'}`} 
         style={{ minWidth: isExpanded ? 'ihherit' : '0' }}
    >
      <nav className="nav flex-column">
        {getMenuItems().map((item, itemKey) => {
          if (!isMenuItemAuthorized(item)) {
            return null;
          }
          const isMainNav = item.id === 'main-nav';
          return (
            <div key={`${itemKey}-${item.id}`} className="nav-item">
              <div
                className="tklmenu-item d-flex justify-content-between align-items-center p-3"
                {...(!isMainNav && { onClick: () => toggleSubmenu(item.id) })}
              >
                {item.icon}
                {isExpanded && (
                  <>
                    {item.link 
                      ? (
                        <a
                          key={item.id}
                          href={item.link}
                          className="nav-link text-white ps-4 py-2"
                          style={item.styles ? {...item.styles} : {}}
                          onClick={(e) => {
                            if (item.onClick) {
                              e.preventDefault();
                              item.onClick();
                            }
                          }}
                        >
                          {item.title}
                        </a>
                      ) 
                      : (<span style={item.styles ? {...item.styles} : {}}>{item.title}</span>)
                    }
                    {!isMainNav && item.children && item.children.length > 0 && (
                      <i className={`bi bi-chevron-${activeItem === item.id ? 'up' : 'down'} ms-auto`} />
                    )}
                  </>
                )}
              </div>
            
            {isExpanded && item.children && item.children.length > 0 && (
              isMainNav ? (
                <div className="tklmenu-sub-item" style={{marginLeft: '26px'}}>
                  {item.children.map((child, childKey) => {
                    if (!isMenuItemAuthorized(child)) {
                      return null;
                    }
                    return (
                      <a
                        key={`${childKey}-${child.id}`}
                        href={child.link}
                        className="nav-link text-white ps-4 py-2"
                        onClick={() => {
                          sessionStorage.removeItem("activityType");
                          if(item.title === "Cohorts"){
                            localStorage.removeItem("microSelected");
                            localStorage.removeItem("kidVisionSelected");
                          }
                          if(item.title === "Master Data"){
                            localStorage.removeItem("microRadioSelect");
                            localStorage.removeItem("kidVisionSelect");
                            localStorage.removeItem("masterRadioValue");
                            localStorage.removeItem("microRadioSelect");
                            localStorage.removeItem("kidVisionSelect");
                          }
                          if(item.title === "Manage Micro Cohort"){
                            localStorage.removeItem("kidVisionSelected");
                            localStorage.setItem("microSelected", "true");
                          }
                          if(item.title === "Manage KidVision Cohort"){
                            localStorage.removeItem("microSelected");
                            localStorage.setItem("kidVisionSelected", "true")
                          }
                        }}
                      >
                        {child.icon && <span style={{ marginRight: 8, display: 'inline-flex', verticalAlign: 'middle' }}>{child.icon}</span>}
                        {child.title}
                      </a>
                    )
                  })}
                </div>
              ) : (
                <div className={`tklmenu-sub-item collapse ${activeItem === item.id ? 'show' : ''}`} style={{marginLeft: '26px'}}>
                  {item.children.map((child, childKey) => {
                    if (!isMenuItemAuthorized(child)) {
                      return null;
                    }
                    return (
                      <a
                        key={`${childKey}-${child.id}`}
                        href={child.link}
                        className="nav-link text-white ps-4 py-2"
                        onClick={() => {
                          sessionStorage.removeItem("activityType");
                          if(item.title === "Cohorts"){
                            localStorage.removeItem("microSelected");
                            localStorage.removeItem("kidVisionSelected");
                          }
                          if(item.title === "Master Data"){
                            localStorage.removeItem("microRadioSelect");
                            localStorage.removeItem("kidVisionSelect");
                            localStorage.removeItem("masterRadioValue");
                            localStorage.removeItem("microRadioSelect");
                            localStorage.removeItem("kidVisionSelect");
                          }
                          if(item.title === "Manage Micro Cohort"){
                            localStorage.removeItem("kidVisionSelected");
                            localStorage.setItem("microSelected", "true");
                          }
                          if(item.title === "Manage KidVision Cohort"){
                            localStorage.removeItem("microSelected");
                            localStorage.setItem("kidVisionSelected", "true")
                          }
                        }}
                      >
                        {child.icon && <span style={{ marginRight: 8, display: 'inline-flex', verticalAlign: 'middle' }}>{child.icon}</span>}
                        {child.title}
                      </a>
                    )
                  })}
                </div>
              )
            )}
          </div>)
        })}
      </nav>
    </div>
  );
};

export default NavMenu;